/** The desk's three panels: is it healthy, its switches, and its numbers.
 *  /redeem/desk shows all three; /redeem shows the numbers. */

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { api, type DeskControls, type DeskDaily, type DeskErrorGroup, type DeskJobHealth, type DeskStat } from '@/api/client'
import { useDeskControls, useDeskHealth, useDeskStats, useRedeemApps } from '@/lib/queries'
import { useInvalidateRedeem } from '@/lib/redeem'
import { TONE_CLASS } from '@/lib/status'
import { cn } from '@/lib/utils'
import {
  Button,
  DataTable,
  ErrorState,
  Skeleton,
  StatusDot,
  Textarea,
  TonePill,
  When,
  errorText,
  useIsDesktop,
  useToast,
  type Column,
} from '@/ui'

const POLL_LABEL: Record<string, string> = {
  redeem_publish: '홍보글 게시',
  redeem_watch: '받은편지함 확인 (3분)',
  redeem_scan: '댓글 스캔 (10분)',
}

const CHANNEL: Record<string, string> = { reddit: 'Reddit', bluesky: 'Bluesky' }

/** The channel a failure came from. A publish error names no platform in its
 *  text, so the job stands in for it. */
function channelOf(g: DeskErrorGroup, job?: string): string {
  if (g.channel) return CHANNEL[g.channel] ?? g.channel
  if (job === 'redeem_publish') return '게시'
  return '데스크'
}

function ChannelTag({ name }: { name: string }) {
  return (
    <span className="shrink-0 rounded bg-status-failed-soft px-1.5 py-0.5 text-micro font-medium text-status-failed">
      {name}
    </span>
  )
}

/** The message whole: a platform error cut short is the one nobody can look up. */
function Message({ text }: { text: string }) {
  return (
    <pre className="mt-1 whitespace-pre-wrap break-words rounded bg-muted px-2 py-1 font-mono text-meta text-foreground">
      {text}
    </pre>
  )
}

function JobProblem({ j }: { j: DeskJobHealth }) {
  const n = `${j.consecutive_errors}${j.consecutive_capped ? '+' : ''}`
  return (
    <li className="space-y-1.5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium">
          {POLL_LABEL[j.job] ?? j.job}{' '}
          <span className="font-normal text-status-failed">
            <When iso={j.failing_since} />
            부터 {n}회 연속 실패
          </span>
        </span>
        <Link to={`/jobs?job=${encodeURIComponent(j.job)}`} className="text-meta text-accent hover:underline">
          실행 기록 →
        </Link>
      </div>
      <div className="text-meta text-muted-foreground">
        마지막 성공 <When iso={j.last_ok_at} /> · 마지막 실행 <When iso={j.last_run_at} />
      </div>
      <ul className="space-y-1.5">
        {j.errors.map((g) => (
          <li key={`${g.channel}:${g.message}`} className="text-meta">
            <div className="flex flex-wrap items-center gap-1.5 text-muted-foreground">
              <ChannelTag name={channelOf(g, j.job)} />
              <span>
                {g.count}회 · 최근 <When iso={g.last_at} />
              </span>
            </div>
            <Message text={g.message} />
          </li>
        ))}
      </ul>
    </li>
  )
}

/** Is the desk still reading, handing out and posting. A job failure used to
 *  land only on the jobs page, which nobody opens — a Bluesky inbox answered
 *  501 every three minutes for hours unnoticed. The strip under every header
 *  raises it; this is the whole report. */
export function DeskHealthPanel() {
  const q = useDeskHealth()
  const h = q.data
  if (q.error && !h) return <ErrorState title="데스크 상태를 읽지 못했다" error={q.error} onRetry={() => q.refetch()} />
  if (!h) return <Skeleton className="h-24" />

  const lastRun = h.jobs
    .map((j) => j.last_run_at)
    .filter((x): x is string => !!x)
    .sort()
    .at(-1)

  if (h.healthy) {
    return (
      <section className="space-y-3 rounded-lg border border-border bg-card p-4">
        <div className="flex items-center gap-2 text-body">
          <StatusDot tone="published" />
          <span>
            데스크 정상
            {lastRun ? (
              <>
                {' '}
                · 마지막 실행 <When iso={lastRun} />
              </>
            ) : (
              ' · 실행 기록 없음'
            )}
          </span>
        </div>
        {h.jobs.length > 0 && (
          <ul className="grid gap-x-6 gap-y-1.5 text-table sm:grid-cols-3">
            {h.jobs.map((j) => (
              <li key={j.job} className="flex flex-wrap items-baseline justify-between gap-x-2">
                <span className="text-muted-foreground">{POLL_LABEL[j.job] ?? j.job}</span>
                <When iso={j.last_run_at} />
              </li>
            ))}
          </ul>
        )}
      </section>
    )
  }

  const bad = h.jobs.filter((j) => j.unhealthy)
  const stuck = h.stuck_requests
  const problems =
    bad.length + (h.failed_requests.count > 0 ? 1 : 0) + (stuck.count > 0 ? 1 : 0) + (h.failed_posts.length > 0 ? 1 : 0)

  return (
    <section role="alert" className="space-y-3 rounded-lg border border-status-failed/40 bg-status-failed-soft/50 p-4 text-body">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-section font-semibold text-status-failed">데스크에 문제 {problems}건</h2>
        <span className="text-meta text-muted-foreground">
          확인 <When iso={h.checked_at} />
          {lastRun && (
            <>
              {' '}
              · 마지막 실행 <When iso={lastRun} />
            </>
          )}
        </span>
      </header>

      {bad.length > 0 && (
        <ul className="space-y-3">
          {bad.map((j) => (
            <JobProblem key={j.job} j={j} />
          ))}
        </ul>
      )}

      {h.failed_requests.count > 0 && (
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="font-medium">실패한 요청 {h.failed_requests.count}건</span>
            <Link to="/redeem/requests?state=failed" className="text-meta text-accent hover:underline">
              요청 보기 →
            </Link>
          </div>
          <ul className="space-y-1.5">
            {h.failed_requests.groups.map((g) => (
              <li key={`${g.channel}:${g.message}`} className="text-meta">
                <div className="flex flex-wrap items-center gap-1.5 text-muted-foreground">
                  <ChannelTag name={channelOf(g)} />
                  <span>
                    {g.count}건 · 최근 <When iso={g.last_at} />
                  </span>
                </div>
                <Message text={g.message} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {stuck.count > 0 && (
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span>
            <span className="font-medium">
              자동 발급이 {stuck.threshold_minutes}분 넘게 멈춘 요청 {stuck.count}건
            </span>{' '}
            <span className="text-meta text-muted-foreground">
              (
              {Object.entries(stuck.by_state)
                .map(([s, n]) => `${s} ${n}`)
                .join(' · ')}
              {stuck.oldest_at && (
                <>
                  {' '}
                  · 가장 오래된 것 <When iso={stuck.oldest_at} />
                </>
              )}
              )
            </span>
          </span>
          <Link to="/redeem/requests?state=new" className="text-meta text-accent hover:underline">
            대기 요청 보기 →
          </Link>
        </div>
      )}

      {h.failed_posts.length > 0 && (
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="font-medium">게시 실패한 홍보글 {h.failed_posts.length}건</span>
            <Link to="/schedule?status=failed&source=redeem" className="text-meta text-accent hover:underline">
              홍보글 보기 →
            </Link>
          </div>
          <ul className="space-y-1.5">
            {h.failed_posts.map((p) => (
              <li key={p.id} className="text-meta">
                <div className="flex flex-wrap items-center gap-1.5 text-muted-foreground">
                  <ChannelTag name={CHANNEL[p.platform] ?? p.platform} />
                  <span>
                    {p.app_name}
                    {p.platform === 'reddit' && p.channel && ` · r/${p.channel}`} · {p.title} · <When iso={p.updated_at} />
                  </span>
                </div>
                <Message text={p.error_msg || '(사유 없음)'} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

/** The desk's two switches: pause Reddit comment reading, and the list of
 *  people and subreddits the desk never comments on. */
export function DeskControlsCard() {
  const q = useDeskControls()
  const invalidate = useInvalidateRedeem()
  const toast = useToast()
  const saved = q.data
  const [text, setText] = useState('')
  useEffect(() => {
    if (saved) setText(saved.blocklist.join('\n'))
  }, [saved])
  const save = useMutation({
    mutationFn: (c: DeskControls) => api.saveRedeemControls(c),
    onSuccess: async () => {
      await invalidate()
    },
    onError: (e) => toast.error(errorText(e)),
  })

  if (q.error && !saved) return <ErrorState title="댓글 감시 설정을 읽지 못했다" error={q.error} onRetry={() => q.refetch()} />
  if (!saved) return <Skeleton className="h-40" />

  const dirty = text.trim() !== saved.blocklist.join('\n')
  const lines = text
    .split(/[\n,]/)
    .map((l) => l.trim())
    .filter(Boolean)

  return (
    <section className="space-y-5 rounded-lg border border-border bg-card p-4">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-section font-semibold">댓글 감시</h2>
          <TonePill tone={saved.watch_paused ? 'publishing' : 'published'} label={saved.watch_paused ? '일시정지 중' : '감시 중'} />
        </div>
        <p className="text-table text-muted-foreground">
          일시정지하면 3분 받은편지함 확인과 10분 댓글 스캔이 Reddit을 읽지 않는다. Bluesky DM과 이미 쌓인 요청 처리는 그대로 돈다.
        </p>
        <Button
          variant={saved.watch_paused ? 'primary' : 'secondary'}
          loading={save.isPending}
          onClick={() =>
            save.mutate(
              { ...saved, watch_paused: !saved.watch_paused },
              { onSuccess: () => toast.success(saved.watch_paused ? '감시를 다시 켰다' : '감시를 일시정지했다') },
            )
          }
        >
          {saved.watch_paused ? '감시 다시 켜기' : 'Reddit 댓글 감시 일시정지'}
        </Button>
      </div>

      <div className="space-y-2">
        <div className="flex items-baseline justify-between gap-2">
          <label htmlFor="desk-blocklist" className="text-section font-semibold">
            차단 목록
          </label>
          <span className="text-meta text-muted-foreground tabular-nums">{saved.blocklist.length}개</span>
        </div>
        <Textarea
          id="desk-blocklist"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={Math.min(10, Math.max(3, lines.length + 1))}
          placeholder={'u/someone\nr/apple'}
          className="font-mono text-meta"
        />
        <p className="text-meta text-muted-foreground">
          한 줄에 하나. <code>u/이름</code>(Bluesky는 <code>@핸들</code>)은 코드도 답글도 없다. <code>r/서브</code>는 코드는 DM으로 가고 그 서브에 공개 댓글만 달지 않는다. 프로필·서브 주소를 붙여넣어도 된다.
        </p>
        <Button
          loading={save.isPending}
          disabled={!dirty}
          onClick={() =>
            save.mutate({ ...saved, blocklist: lines }, { onSuccess: () => toast.success('차단 목록을 저장했다') })
          }
        >
          차단 목록 저장
        </Button>
        {save.error && <p className="text-table text-status-failed">{errorText(save.error)}</p>}
      </div>
    </section>
  )
}

const NUM = 'tabular-nums'

function Num({ n, tone, dim = true }: { n: number; tone?: string; dim?: boolean }) {
  return <span className={cn(NUM, n === 0 && dim ? 'text-muted-foreground/60' : tone)}>{n.toLocaleString()}</span>
}

/** Today at the handout desk, per app: what went out, what came in, who is
 *  still waiting — the numbers to check before handing out more. The pool is
 *  in the same row because that is what limits it. With `chart`, the daily
 *  bars follow. */
export function DeskStatsPanel({ days = 7, chart = false }: { days?: number; chart?: boolean }) {
  const q = useDeskStats(days)
  const desktop = useIsDesktop()
  const apps = useRedeemApps().data?.items ?? []
  const items = q.data?.items ?? []
  const daily = q.data?.daily ?? []
  const totals = items.reduce(
    (a, s) => ({
      issued: a.issued + s.issued_today,
      requests: a.requests + s.requests_today,
      pending: a.pending + s.pending_new,
      failed: a.failed + s.failed_open,
    }),
    { issued: 0, requests: 0, pending: 0, failed: 0 },
  )

  const columns: Column<DeskStat>[] = [
    {
      key: 'name',
      header: '앱',
      card: 'title',
      cell: (s) => (
        <span className="font-medium">
          {s.name}
          {s.auto_handout === 1 && (
            <span className="ml-1.5 rounded bg-muted px-1 py-0.5 align-middle text-micro font-normal text-muted-foreground">자동</span>
          )}
        </span>
      ),
    },
    { key: 'today', header: '오늘 발급', align: 'right', cell: (s) => <Num n={s.issued_today} tone="font-semibold" /> },
    { key: 'req', header: '오늘 요청', align: 'right', cell: (s) => <Num n={s.requests_today} /> },
    { key: 'replied', header: '응대', align: 'right', cell: (s) => <Num n={s.replied_today} /> },
    { key: 'new', header: '대기', align: 'right', cell: (s) => <Num n={s.pending_new} tone="font-semibold text-accent" /> },
    { key: 'failed', header: '실패', align: 'right', cell: (s) => <Num n={s.failed_open} tone="font-semibold text-status-failed" /> },
    { key: 'window', header: `${days}일 발급`, align: 'right', cell: (s) => <Num n={s.issued_window} /> },
    {
      key: 'left',
      header: '남은 코드',
      align: 'right',
      // The pool is never dimmed: an empty one is the alarm, not the quiet case.
      cell: (s) => <span className={cn(NUM, s.available < 20 ? cn('font-semibold', TONE_CLASS.publishing.text) : 'text-muted-foreground')}>{s.available.toLocaleString()}</span>,
    },
    {
      key: 'last',
      header: '마지막 발급',
      align: 'right',
      cell: (s) => (s.last_issued_at ? <When iso={s.last_issued_at} className="text-meta text-muted-foreground" /> : '—'),
    },
  ]

  if (q.error && !q.data) return <ErrorState title="데스크 통계를 읽지 못했다" error={q.error} onRetry={() => q.refetch()} />
  if (q.isPending) return <Skeleton className="h-40" />
  if (items.length === 0) return null

  return (
    <section aria-label="데스크 통계" className="space-y-3">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-section font-semibold">
          오늘 발급 <span className="text-stat tabular-nums">{totals.issued}</span>
          <span className="ml-2 text-table font-normal text-muted-foreground">
            요청 {totals.requests} · 대기 {totals.pending}
            {totals.failed > 0 && ` · 실패 ${totals.failed}`}
          </span>
        </h2>
        <span className="text-meta text-muted-foreground">최근 {days}일</span>
      </header>
      {desktop ? (
        <DataTable label="앱별 데스크 통계" columns={columns} rows={items} rowKey={(s) => s.app_id} />
      ) : (
        <StatLines items={items} days={days} />
      )}
      {chart && <DailyBars daily={daily} appNames={Object.fromEntries(apps.map((a) => [a.app.slug, a.app.name]))} apps={apps.map((a) => a.app)} />}
    </section>
  )
}

/** The phone's form of the table: one line per app with what needs eyes
 *  (waiting, failed, the pool), the week and the last handout under it.
 *  Nine label/value rows per app made the page a scroll of zeros. */
function StatLines({ items, days }: { items: DeskStat[]; days: number }) {
  return (
    <ul aria-label="앱별 데스크 통계" className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
      {items.map((s) => (
        <li key={s.app_id} className="px-3 py-2.5">
          <div className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 truncate font-medium">
              {s.name}
              {s.auto_handout === 1 && (
                <span className="ml-1.5 rounded bg-muted px-1 py-0.5 align-middle text-micro font-normal text-muted-foreground">자동</span>
              )}
            </span>
            <span className="shrink-0 text-table tabular-nums">
              오늘 <span className="font-semibold">{s.issued_today}</span>장
            </span>
          </div>
          <div className="mt-0.5 flex flex-wrap gap-x-2 text-meta text-muted-foreground">
            <span>요청 {s.requests_today}</span>
            {s.pending_new > 0 && <span className="font-medium text-accent">대기 {s.pending_new}</span>}
            {s.failed_open > 0 && <span className="font-medium text-status-failed">실패 {s.failed_open}</span>}
            <span className={cn(s.available < 20 && cn('font-medium', TONE_CLASS.publishing.text))}>남은 {s.available}</span>
            <span>
              {days}일 {s.issued_window}
            </span>
            {s.last_issued_at && (
              <span>
                마지막 <When iso={s.last_issued_at} />
              </span>
            )}
          </div>
        </li>
      ))}
    </ul>
  )
}

/** Codes out per day, every app together. The count sits above its bar: a
 *  bar alone says which day was busier, not how many codes left the pool. */
function DailyBars({
  daily,
  appNames,
  apps,
}: {
  daily: DeskDaily[]
  appNames: Record<string, string>
  apps: { slug: string; product_name: string; product_id: string; grant_kind: string; grant_period: string }[]
}) {
  const [open, setOpen] = useState<string | null>(null)
  const byDay = useMemo(() => {
    const days = [...new Set(daily.map((d) => d.day))].sort()
    return days.map((day) => {
      const rows = daily.filter((d) => d.day === day)
      return { day, rows, n: rows.reduce((a, d) => a + d.issued, 0) }
    })
  }, [daily])
  if (byDay.length < 2) return null
  const peak = Math.max(1, ...byDay.map((d) => d.n))
  const shown = byDay.find((d) => d.day === open)
  const perApp = Object.entries(
    daily.reduce<Record<string, number>>((acc, d) => {
      acc[d.slug] = (acc[d.slug] ?? 0) + d.issued
      return acc
    }, {}),
  ).sort((a, b) => b[1] - a[1])

  return (
    <div className="space-y-2 rounded-lg border border-border bg-card p-4">
      <div className="flex items-baseline justify-between text-meta text-muted-foreground">
        <span>일별 발급</span>
        <span className="tabular-nums">
          합계 {byDay.reduce((a, d) => a + d.n, 0)}장 · 하루 최다 {peak}장
        </span>
      </div>
      <div className="flex items-end gap-1">
        {byDay.map(({ day, n }) => (
          <button
            key={day}
            type="button"
            aria-pressed={open === day}
            aria-label={`${day} ${n}장`}
            onClick={() => setOpen((d) => (d === day ? null : day))}
            onMouseEnter={() => setOpen(day)}
            className="flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
          >
            <span className={cn('text-micro tabular-nums', n === 0 ? 'text-muted-foreground' : 'font-medium')}>{n}</span>
            <span
              className={cn('w-full rounded-t', open === day ? 'bg-accent' : 'bg-accent/60')}
              style={{ height: `${Math.max(2, (n / peak) * 56)}px` }}
            />
            <span className="text-micro text-muted-foreground">{day.slice(5)}</span>
          </button>
        ))}
      </div>
      {shown && (
        <div className="rounded-md bg-muted px-3 py-2 text-table">
          <div className="flex items-baseline justify-between">
            <span className="font-medium">{shown.day}</span>
            <span className="tabular-nums text-muted-foreground">{shown.n}장</span>
          </div>
          {shown.rows.filter((r) => r.issued > 0).length === 0 ? (
            <p className="text-meta text-muted-foreground">발급 없음</p>
          ) : (
            <ul className="mt-1 space-y-1">
              {shown.rows
                .filter((r) => r.issued > 0)
                .sort((a, b) => b.issued - a.issued)
                .map((r) => {
                  const app = apps.find((a) => a.slug === r.slug)
                  return (
                    <li key={r.slug} className="flex flex-wrap items-baseline justify-between gap-x-2">
                      <span>
                        <span className="font-medium">{appNames[r.slug] ?? r.slug}</span>
                        <span className="ml-2 text-meta text-muted-foreground">
                          {app?.product_name || app?.product_id || '—'}
                          {app?.grant_kind === 'trial' && app.grant_period ? ` · ${app.grant_period}` : ''}
                        </span>
                      </span>
                      <span className="tabular-nums">{r.issued}장</span>
                    </li>
                  )
                })}
            </ul>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-meta text-muted-foreground">
        {perApp.map(([slug, n]) => (
          <span key={slug}>
            {slug} <b className="tabular-nums text-foreground">{n}</b>
          </span>
        ))}
      </div>
    </div>
  )
}
